/**
 * Readable prose out of a generated summary.
 *
 * The summaries and briefs come back as Markdown, and a search result is the one
 * place they are shown with no renderer behind them — so a snippet reads
 * `#### Overview of "Fawz" Application - **Purpose**` where it should read
 * `Overview of "Fawz" Application - Purpose`. Syntax nobody typed and nobody
 * searches for is noise in a two-line window.
 *
 * Stripped at INDEX time rather than at display time, and that is the whole
 * reason this file exists in this shape: highlight ranges are offsets into the
 * indexed string, so cleaning the text afterwards would slide every mark off the
 * word it belongs to. Search what will be shown; show what was searched.
 *
 * Deliberately not a Markdown parser. It handles the constructs a summariser
 * actually emits and leaves anything else alone — a stray asterisk in prose
 * survives, which is the right failure for a display-only cleanup.
 */

/** Line-anchored constructs: what sits at the START of a line and means
 *  "heading", "list item", "quote" rather than being part of the sentence. */
const LINE_RULES: readonly (readonly [RegExp, string])[] = [
  // Fenced code delimiters, and horizontal rules of any of the three characters.
  [/^\s*(?:```|~~~).*$/gmu, ''],
  [/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/gmu, ''],
  // ATX headings. The hashes go; the heading text stays as its own sentence.
  [/^\s{0,3}#{1,6}\s+/gmu, ''],
  // Blockquote markers, however deeply nested.
  [/^\s*>+\s?/gmu, ''],
  // Bullets and ordered markers. The text after them is the content.
  [/^\s*(?:[-*+]|\d+[.)])\s+/gmu, ''],
];

/** Inline constructs, applied after the line rules so a bolded list item has
 *  already lost its bullet. */
const INLINE_RULES: readonly (readonly [RegExp, string])[] = [
  // Images before links: `![alt](src)` is a link pattern with a `!` in front, so
  // the link rule would otherwise leave the `!` stranded.
  [/!\[([^\]]*)\]\([^)]*\)/gu, '$1'],
  [/\[([^\]]+)\]\([^)]*\)/gu, '$1'],
  // Emphasis, longest marker first: `**x**` must not be read as two `*x*`.
  [/\*\*([^*]+)\*\*/gu, '$1'],
  [/__([^_]+)__/gu, '$1'],
  [/(?<![\p{L}\p{N}])\*([^*\n]+)\*(?![\p{L}\p{N}])/gu, '$1'],
  [/(?<![\p{L}\p{N}])_([^_\n]+)_(?![\p{L}\p{N}])/gu, '$1'],
  // Inline code. The code itself is the readable part; the ticks are not.
  [/`([^`\n]+)`/gu, '$1'],
];

/**
 * Strip Markdown syntax and flatten to a single line.
 *
 * The flattening is not incidental: a snippet is two clamped lines, and a raw
 * newline inside one renders as a space anyway — collapsing here means the
 * window `makeSnippet` measures is the window the reader gets.
 */
export function toPlainText(raw: string): string {
  let text = raw;
  for (const [pattern, replacement] of LINE_RULES) text = text.replace(pattern, replacement);
  for (const [pattern, replacement] of INLINE_RULES) text = text.replace(pattern, replacement);
  return collapse(text);
}

/** Every run of whitespace — newlines included — as one space. */
export function collapse(raw: string): string {
  return raw.replace(/\s+/gu, ' ').trim();
}
